"use strict";
// packages/shared-schemas/src/websiteConnection.ts
//
// Website connection: one tenant website that may post forms to VALUTTEN.
//
//   companies/{c}/website_connections/{id}    the connection (super_admin edits)
//   website_site_keys/{siteKey}               top-level lookup index, server-only:
//                                             the public endpoint resolves a key in one read
//
// A connection carries a public site key ('wsk_' + 32 hex, NOT a secret: it
// is printed in the website's HTML), the exact origins allowed to post, the
// bot check settings, a protection mode and which forms are on. Rotation
// keeps up to three previous keys alive for 24 hours, the same semantics as
// buildRotatedWebhookConfig (services/client-functions/src/leads/webhookHealth.ts).
//
// Spec: docs/plans/2026-10-23-WEBSITE-FORMS-PLATFORM.md §2.1.
Object.defineProperty(exports, "__esModule", { value: true });
exports.BOT_SCORE_BUCKETS = exports.WebsiteSiteKeyIndexSchema = exports.WebsiteConnectionInputSchema = exports.WebsiteConnectionSchema = exports.SITE_KEY_ROTATION_GRACE_MS = exports.MAX_PREVIOUS_SITE_KEYS = exports.PreviousSiteKeySchema = exports.WebsiteConnectionFormsSchema = exports.EnquiryVariantsSchema = exports.EnquiryVariantSchema = exports.EnquiryVariantPresetSchema = exports.MAX_ENQUIRY_VARIANTS = exports.ENQUIRY_VARIANT_KEY_PATTERN = exports.BotCheckConfigSchema = exports.AllowedOriginsSchema = exports.MAX_ALLOWED_ORIGINS = exports.SITE_KEY_PATTERN = exports.WEBSITE_CONNECTION_ID_PATTERN = exports.DEFAULT_PROTECTION_MODE = exports.ProtectionModeSchema = exports.PROTECTION_MODES = exports.WebsiteFormKind = exports.WEBSITE_FORM_KINDS = exports.WEBSITE_CONNECTION_STATS_SUBCOLLECTION = exports.WEBSITE_SUBMISSIONS_COLLECTION = exports.WEBSITE_SITE_KEYS_COLLECTION = exports.WEBSITE_CONNECTIONS_SUBCOLLECTION = void 0;
exports.isWebsiteConnectionId = isWebsiteConnectionId;
exports.isSiteKey = isSiteKey;
exports.normaliseAllowedOrigin = normaliseAllowedOrigin;
exports.isCanonicalAllowedOrigin = isCanonicalAllowedOrigin;
exports.isValuttenOrigin = isValuttenOrigin;
exports.isOriginAllowed = isOriginAllowed;
exports.resolveEnquiryTarget = resolveEnquiryTarget;
exports.isFormEnabled = isFormEnabled;
exports.isSiteKeyIndexLive = isSiteKeyIndexLive;
exports.planSiteKeyRotation = planSiteKeyRotation;
exports.websiteConnectionStatsDocId = websiteConnectionStatsDocId;
exports.botScoreBucket = botScoreBucket;
const zod_1 = require("zod");
// ─── Collections ────────────────────────────────────────────────────────────
exports.WEBSITE_CONNECTIONS_SUBCOLLECTION = 'website_connections';
exports.WEBSITE_SITE_KEYS_COLLECTION = 'website_site_keys';
exports.WEBSITE_SUBMISSIONS_COLLECTION = 'website_submissions';
exports.WEBSITE_CONNECTION_STATS_SUBCOLLECTION = 'website_connection_stats';
// ─── Enums ──────────────────────────────────────────────────────────────────
exports.WEBSITE_FORM_KINDS = ['enquiry', 'newsletter', 'referral'];
exports.WebsiteFormKind = zod_1.z.enum(exports.WEBSITE_FORM_KINDS);
exports.PROTECTION_MODES = ['off', 'observe', 'enforce'];
exports.ProtectionModeSchema = zod_1.z.enum(exports.PROTECTION_MODES);
/** Mode a new connection starts in: checks run and are recorded, nothing is refused. */
exports.DEFAULT_PROTECTION_MODE = 'observe';
// ─── Identifiers ────────────────────────────────────────────────────────────
exports.WEBSITE_CONNECTION_ID_PATTERN = /^wc_[a-z0-9]{10,40}$/;
exports.SITE_KEY_PATTERN = /^wsk_[0-9a-f]{32}$/;
function isWebsiteConnectionId(value) {
    return typeof value === 'string' && exports.WEBSITE_CONNECTION_ID_PATTERN.test(value);
}
function isSiteKey(value) {
    return typeof value === 'string' && exports.SITE_KEY_PATTERN.test(value);
}
// ─── Allowed origins ────────────────────────────────────────────────────────
exports.MAX_ALLOWED_ORIGINS = 10;
/**
 * Normalise what an admin typed into the exact origin the browser sends:
 * trimmed, one trailing slash tolerated, scheme and host lower-cased by URL
 * parsing, default port dropped. Anything beyond scheme://host[:port] is
 * refused rather than silently cut off, so the stored value is exactly what
 * the admin meant.
 */
function normaliseAllowedOrigin(raw) {
    const input = (raw ?? '').trim();
    if (input === '')
        return { ok: false, reason: 'empty' };
    if (input.includes('*'))
        return { ok: false, reason: 'wildcard' };
    let url;
    try {
        url = new URL(input);
    }
    catch {
        return { ok: false, reason: 'not_url' };
    }
    if (url.protocol !== 'https:')
        return { ok: false, reason: 'not_https' };
    if (url.username || url.password)
        return { ok: false, reason: 'has_credentials' };
    // An empty "?" or "#" parses to an empty search/hash, so check the text too.
    if (url.search || url.hash || input.includes('?') || input.includes('#'))
        return { ok: false, reason: 'has_path' };
    if (url.pathname !== '/' && url.pathname !== '')
        return { ok: false, reason: 'has_path' };
    return { ok: true, origin: url.origin };
}
/** True when `value` is already a canonical allowed origin (what the schema stores). */
function isCanonicalAllowedOrigin(value) {
    const r = normaliseAllowedOrigin(value);
    return r.ok && r.origin === value;
}
/** True for VALUTTEN's own domains, which no one may add to a website connection (founder decision D1). */
function isValuttenOrigin(origin) {
    try {
        const host = new URL(origin).hostname.toLowerCase();
        return host === 'valutten.com' || host.endsWith('.valutten.com');
    }
    catch {
        return false;
    }
}
/**
 * Exact-origin check for a request's Origin header against a connection.
 * No suffix or wildcard matching: "https://evil-example.com" never matches
 * "https://example.com".
 */
function isOriginAllowed(origin, allowedOrigins) {
    if (!origin)
        return false;
    const r = normaliseAllowedOrigin(origin);
    return r.ok && allowedOrigins.includes(r.origin);
}
exports.AllowedOriginsSchema = zod_1.z
    .array(zod_1.z.string().url())
    .min(1)
    .max(exports.MAX_ALLOWED_ORIGINS)
    .superRefine((origins, ctx) => {
    const seen = new Set();
    origins.forEach((o, i) => {
        if (!isCanonicalAllowedOrigin(o)) {
            const r = normaliseAllowedOrigin(o);
            ctx.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                path: [i],
                message: r.ok
                    ? `Use the exact origin ${r.origin}`
                    : `Use an exact https origin with no path or wildcard (${r.reason})`,
            });
        }
        if (seen.has(o))
            ctx.addIssue({ code: zod_1.z.ZodIssueCode.custom, path: [i], message: 'Duplicate origin' });
        seen.add(o);
    });
});
// ─── Connection ─────────────────────────────────────────────────────────────
exports.BotCheckConfigSchema = zod_1.z.object({
    provider: zod_1.z.literal('recaptcha_enterprise'),
    minScore: zod_1.z.number().min(0).max(1).default(0.5),
});
// ─── Enquiry form variants ──────────────────────────────────────────────────
//
// One site often has several enquiry forms (home loan, commercial, off the
// plan, refinance). A variant is a named enquiry form on the connection with
// its own service category, office and preset answers. The site picks one by
// sending `data.variant` = the variant key; a submission without a variant
// (or with a key the connection no longer has) uses the connection's own
// enquiry settings, so removing a variant never breaks a live form.
// Preset answers are applied only where the visitor gave none, and are
// checked against the tenant's options at submit like any other answer.
exports.ENQUIRY_VARIANT_KEY_PATTERN = /^[a-z][a-z0-9_-]{0,39}$/;
exports.MAX_ENQUIRY_VARIANTS = 20;
exports.EnquiryVariantPresetSchema = zod_1.z.object({
    loanPurpose: zod_1.z.string().max(64).nullable().optional(),
    propertyUse: zod_1.z.string().max(64).nullable().optional(),
    borrowerSituations: zod_1.z.array(zod_1.z.string().max(64)).max(20).optional(),
    loanStructure: zod_1.z.string().max(64).nullable().optional(),
    loanAmount: zod_1.z.string().max(64).nullable().optional(),
});
exports.EnquiryVariantSchema = zod_1.z.object({
    key: zod_1.z.string().regex(exports.ENQUIRY_VARIANT_KEY_PATTERN, 'Use lowercase letters, digits, - or _'),
    label: zod_1.z.string().trim().min(1).max(80),
    serviceCategory: zod_1.z.string().nullable(),
    officeId: zod_1.z.string().nullable(),
    preset: exports.EnquiryVariantPresetSchema.default({}),
});
exports.EnquiryVariantsSchema = zod_1.z
    .array(exports.EnquiryVariantSchema)
    .max(exports.MAX_ENQUIRY_VARIANTS)
    .superRefine((vs, ctx) => {
    const seen = new Set();
    vs.forEach((v, i) => {
        if (seen.has(v.key))
            ctx.addIssue({ code: zod_1.z.ZodIssueCode.custom, path: [i, 'key'], message: `Duplicate form key '${v.key}'` });
        seen.add(v.key);
    });
});
/**
 * The service, office and preset answers an enquiry submission routes on:
 * the named variant when the connection has it, else the connection's own
 * enquiry settings. Pure; the one resolver the endpoint and the connection
 * page's routing preview share.
 */
function resolveEnquiryTarget(enquiry, variantKey) {
    const variant = variantKey ? (enquiry.variants ?? []).find((v) => v.key === variantKey) ?? null : null;
    if (variant)
        return { variant, serviceCategory: variant.serviceCategory ?? null, officeId: variant.officeId ?? null, preset: variant.preset ?? {} };
    return { variant: null, serviceCategory: enquiry.serviceCategory ?? null, officeId: enquiry.officeId ?? null, preset: {} };
}
exports.WebsiteConnectionFormsSchema = zod_1.z.object({
    enquiry: zod_1.z.object({
        enabled: zod_1.z.boolean(),
        serviceCategory: zod_1.z.string().nullable(),
        officeId: zod_1.z.string().nullable(),
        /** Optional so connections saved before variants existed still parse. */
        variants: exports.EnquiryVariantsSchema.optional(),
    }),
    newsletter: zod_1.z.object({ enabled: zod_1.z.boolean(), welcomeEmail: zod_1.z.boolean().default(true) }),
    referral: zod_1.z.object({
        enabled: zod_1.z.boolean(),
        partnerId: zod_1.z.string().nullable(),
        /**
         * When true, each website referral also becomes a lead in this company's
         * inbox and is routed exactly like an enquiry (service, office and the
         * referrer's answers below). Off (absent) = the Partner Network desk only,
         * as before.
         */
        routeAsLead: zod_1.z.boolean().optional(),
        /** Service category and office the referral lead routes on. */
        serviceCategory: zod_1.z.string().nullable().optional(),
        officeId: zod_1.z.string().nullable().optional(),
    }),
});
exports.PreviousSiteKeySchema = zod_1.z.object({ key: zod_1.z.string(), expiresAt: zod_1.z.unknown() });
exports.MAX_PREVIOUS_SITE_KEYS = 3;
exports.SITE_KEY_ROTATION_GRACE_MS = 24 * 60 * 60 * 1000;
exports.WebsiteConnectionSchema = zod_1.z.object({
    id: zod_1.z.string().regex(exports.WEBSITE_CONNECTION_ID_PATTERN),
    companyId: zod_1.z.string(),
    label: zod_1.z.string().min(1).max(80),
    /** Informational; staging connections exist only in valutten-stage. */
    environment: zod_1.z.enum(['production', 'staging']),
    /** https only, exact origin, no path, no wildcard. */
    allowedOrigins: exports.AllowedOriginsSchema,
    /** 'observe' on create (DEFAULT_PROTECTION_MODE). */
    mode: exports.ProtectionModeSchema,
    bot: exports.BotCheckConfigSchema,
    forms: exports.WebsiteConnectionFormsSchema,
    /** Public, 'wsk_' + 32 hex. NOT a secret. */
    siteKey: zod_1.z.string().regex(exports.SITE_KEY_PATTERN),
    previousSiteKeys: zod_1.z.array(exports.PreviousSiteKeySchema).max(exports.MAX_PREVIOUS_SITE_KEYS),
    generation: zod_1.z.number().int(),
    status: zod_1.z.enum(['active', 'disabled']),
    welcome: zod_1.z.object({ pausedReason: zod_1.z.string().nullable(), pausedAt: zod_1.z.unknown().nullable() }),
    /** Daily synthetic submission (website canary, spec §10). Off unless set. */
    canary: zod_1.z.object({ enabled: zod_1.z.boolean().default(false) }).default({ enabled: false }),
    /**
     * Growth wave 1: keep Meta cookie ids and the stored last touch on enquiries.
     * Written only by scripts/onboarding/set-growth-config.ts; anything but a
     * real boolean reads as false. Not part of WebsiteConnectionInputSchema.
     */
    captureAdIdentifiers: zod_1.z.boolean().catch(false).optional(),
    createdAt: zod_1.z.unknown(),
    createdBy: zod_1.z.string(),
    updatedAt: zod_1.z.unknown(),
    updatedBy: zod_1.z.string(),
});
/**
 * The fields an admin sets on create / update. The server owns id,
 * companyId, siteKey, previousSiteKeys, generation, status, welcome and the
 * audit fields.
 */
exports.WebsiteConnectionInputSchema = zod_1.z.object({
    label: zod_1.z.string().trim().min(1).max(80),
    environment: zod_1.z.enum(['production', 'staging']),
    allowedOrigins: exports.AllowedOriginsSchema,
    mode: exports.ProtectionModeSchema.default(exports.DEFAULT_PROTECTION_MODE),
    bot: zod_1.z.object({ minScore: zod_1.z.number().min(0).max(1).default(0.5) }).default({ minScore: 0.5 }),
    forms: exports.WebsiteConnectionFormsSchema,
});
/** True when the connection accepts `form` right now. */
function isFormEnabled(connection, form) {
    return connection.status === 'active' && connection.forms[form]?.enabled === true;
}
// ─── Site key index ─────────────────────────────────────────────────────────
/** website_site_keys/{siteKey}. `expiresAt` is set only on a rotated-out key. */
exports.WebsiteSiteKeyIndexSchema = zod_1.z.object({
    companyId: zod_1.z.string().min(1),
    connectionId: zod_1.z.string().regex(exports.WEBSITE_CONNECTION_ID_PATTERN),
    active: zod_1.z.boolean(),
    expiresAt: zod_1.z.unknown().nullable(),
});
/**
 * Is an index entry usable at `nowMs`? `toMillis` reads the stored
 * timestamp (Firestore Timestamp, Date or epoch ms) without this package
 * depending on Firestore.
 */
function isSiteKeyIndexLive(entry, nowMs, toMillis) {
    if (!entry.active)
        return false;
    if (entry.expiresAt === null || entry.expiresAt === undefined)
        return true;
    const ms = toMillis(entry.expiresAt);
    return ms !== null && ms > nowMs;
}
/**
 * Plan a site key rotation (pure). The current key moves to previous keys
 * with a 24 hour grace; previous keys already expired, duplicates, and any
 * beyond the newest three are dropped and returned in `retiredKeys` so the
 * caller deletes or deactivates their index entries in the same batch.
 */
function planSiteKeyRotation(current, newKey, nowMs, clock) {
    if (!isSiteKey(current.siteKey))
        throw new RangeError('No existing site key is configured.');
    if (!isSiteKey(newKey))
        throw new RangeError('The new site key is malformed.');
    if (newKey === current.siteKey)
        throw new RangeError('The new site key must differ from the current one.');
    const graceExpiresAtMs = nowMs + exports.SITE_KEY_ROTATION_GRACE_MS;
    const seen = new Set([newKey]);
    const previousSiteKeys = [];
    const retiredKeys = [];
    const candidates = [
        { key: current.siteKey, expiresAt: clock.fromMillis(graceExpiresAtMs), ms: graceExpiresAtMs },
        ...current.previousSiteKeys.map((p) => ({ key: p.key, expiresAt: p.expiresAt, ms: clock.toMillis(p.expiresAt) })),
    ];
    for (const c of candidates) {
        if (!c.key || seen.has(c.key))
            continue;
        seen.add(c.key);
        if (c.ms === null || c.ms <= nowMs || previousSiteKeys.length === exports.MAX_PREVIOUS_SITE_KEYS) {
            retiredKeys.push(c.key);
            continue;
        }
        previousSiteKeys.push({ key: c.key, expiresAt: c.expiresAt });
    }
    return {
        siteKey: newKey,
        previousSiteKeys,
        generation: (Number.isFinite(current.generation) ? current.generation : 0) + 1,
        graceExpiresAtMs,
        retiredKeys,
    };
}
// ─── Stats ──────────────────────────────────────────────────────────────────
/** companies/{c}/website_connection_stats/{connectionId}_{yyyymmdd} doc id (Brisbane day supplied by the caller). */
function websiteConnectionStatsDocId(connectionId, yyyymmdd) {
    if (!/^\d{8}$/.test(yyyymmdd))
        throw new RangeError('yyyymmdd must be 8 digits');
    return `${connectionId}_${yyyymmdd}`;
}
/** Bot score buckets recorded per day (lower bound inclusive). */
exports.BOT_SCORE_BUCKETS = ['0.0', '0.1', '0.3', '0.5', '0.7', '0.9'];
function botScoreBucket(score) {
    if (!(score >= 0.1))
        return '0.0';
    if (score < 0.3)
        return '0.1';
    if (score < 0.5)
        return '0.3';
    if (score < 0.7)
        return '0.5';
    if (score < 0.9)
        return '0.7';
    return '0.9';
}
